import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static targets = ["results", "sentinel", "form", "gridBtn", "listBtn", "count"]
  static values = { apiUrl: String }

  connect() {
    loadTranslations()
    this.page = 1
    this.hasMore = false
    this.loading = false
    this.filterTimeout = null
    this.viewMode = (typeof localStorage !== "undefined" && localStorage.getItem("clubs_view_mode")) || "grid"
    this._applyViewMode()
    this._setupInfiniteScroll()
    this._setupFilters()
    this._fetchResults(1, true)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.hasFormTarget && this._boundFilter) {
      this.formTarget.removeEventListener("input", this._boundFilter)
      this.formTarget.removeEventListener("change", this._boundFilter)
      this.formTarget.removeEventListener("submit", this._boundSubmit)
    }
  }

  showGrid() {
    this.viewMode = "grid"
    this._applyViewMode()
    if (typeof localStorage !== "undefined") localStorage.setItem("clubs_view_mode", "grid")
  }

  showList() {
    this.viewMode = "list"
    this._applyViewMode()
    if (typeof localStorage !== "undefined") localStorage.setItem("clubs_view_mode", "list")
  }

  _applyViewMode() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.remove("club-results--grid", "club-results--list")
    this.resultsTarget.classList.add(`club-results--${this.viewMode}`)
    if (this.hasGridBtnTarget) this.gridBtnTarget.classList.toggle("active", this.viewMode === "grid")
    if (this.hasListBtnTarget) this.listBtnTarget.classList.toggle("active", this.viewMode === "list")
  }

  _setupFilters() {
    if (!this.hasFormTarget) return
    this._boundFilter = this._onFilterChange.bind(this)
    this._boundSubmit = this._onSubmit.bind(this)
    this.formTarget.addEventListener("input", this._boundFilter)
    this.formTarget.addEventListener("change", this._boundFilter)
    this.formTarget.addEventListener("submit", this._boundSubmit)
  }

  _onFilterChange(event) {
    if (event.target.name === "search_mode") return
    const isCityInput = event.target.name === "city" && event.type === "input"
    if (isCityInput) {
      const val = (event.target.value || "").trim()
      if (val.length >= 3 || val.length === 0) {
        clearTimeout(this.filterTimeout)
        this.filterTimeout = setTimeout(() => this._fetchResults(1, true), 400)
      }
      return
    }
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this._fetchResults(1, true), 150)
  }

  _onSubmit(event) {
    event.preventDefault()
    this._fetchResults(1, true)
  }

  _setupInfiniteScroll() {
    if (!this.hasSentinelTarget) return
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.hasMore && !this.loading) {
        this._fetchResults(this.page + 1, false)
      }
    }, { rootMargin: "200px", threshold: 0.1 })
    this.observer.observe(this.sentinelTarget)
  }

  async _fetchResults(page, replace) {
    if (this.loading) return
    this.loading = true

    if (replace) {
      this.resultsTarget.style.opacity = "0.4"
      this.resultsTarget.style.pointerEvents = "none"
    } else if (this.hasSentinelTarget) {
      this.sentinelTarget.innerHTML = '<div class="text-center py-3"><i class="fa-solid fa-spinner fa-spin"></i></div>'
    }

    try {
      const params = this._buildParams(page)
      const json = await apiGet(this.apiUrlValue, params)
      const { data, meta } = json

      if (replace) {
        this.resultsTarget.innerHTML = data.length > 0
          ? data.map(c => this._renderClubItem(c)).join("")
          : this._renderEmpty()
      } else {
        data.forEach(c => this.resultsTarget.insertAdjacentHTML("beforeend", this._renderClubItem(c)))
      }

      this.page = meta.current_page
      this.hasMore = meta.has_more
      if (this.hasCountTarget) this.countTarget.textContent = String(meta.total_count)

      if (this.hasSentinelTarget) {
        this.sentinelTarget.innerHTML = this.hasMore
          ? '<div class="text-center py-3"><i class="fa-solid fa-spinner fa-spin"></i></div>'
          : ""
      }
    } catch (error) {
      console.error("Error fetching clubs:", error)
      if (this.hasSentinelTarget) this.sentinelTarget.innerHTML = ""
    } finally {
      this.loading = false
      this.resultsTarget.style.opacity = ""
      this.resultsTarget.style.pointerEvents = ""
      this._checkSentinelVisibility()
    }
  }

  _checkSentinelVisibility() {
    if (!this.hasSentinelTarget || !this.hasMore || this.loading) return

    const rect = this.sentinelTarget.getBoundingClientRect()
    const inView = rect.top < window.innerHeight + 200
    if (inView) {
      this._fetchResults(this.page + 1, false)
    }
  }

  _buildParams(page) {
    if (!this.hasFormTarget) return { page }
    const formData = new FormData(this.formTarget)
    const params = {}
    for (const [key, value] of formData.entries()) {
      if (value) {
        if (key.endsWith("[]")) {
          const k = key.slice(0, -2)
          if (!params[k]) params[k] = []
          params[k].push(value)
        } else {
          params[key] = value
        }
      }
    }
    params.page = page
    return params
  }

  _renderClubItem(club) {
    const url = `/clubs/${escapeHtml(club.slug)}`
    const feds = (club.federations || []).map(f => `<span class="badge bg-primary me-1">${escapeHtml(f)}</span>`).join("")
    const membersText = `${club.members_count || 0} ${t("members", { default: "członków" })}`
    const locationHtml = club.city ? `<i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(club.city)}` : ""

    return `<div class="club-result-item">
      <div class="club-card-wrapper">
        <a href="${url}" class="text-decoration-none text-reset d-block">
          <div class="club-card p-3 bg-white rounded shadow-sm mb-2">
            <div class="d-flex align-items-center gap-3">
              <div class="club-card__logo flex-shrink-0">
                <div class="club-show__logo-placeholder"><i class="fa-solid fa-building fa-2x text-muted"></i></div>
              </div>
              <div class="flex-grow-1 min-width-0">
                <h6 class="mb-1">${escapeHtml(club.name)}</h6>
                <div class="mb-1">${feds}</div>
                <p class="text-muted small mb-0">
                  ${locationHtml}
                  ${club.city && club.members_count ? " &bull; " : ""}
                  <i class="fa-solid fa-users me-1"></i>${membersText}
                </p>
              </div>
              <div class="flex-shrink-0 text-muted"><i class="fa-solid fa-chevron-right"></i></div>
            </div>
          </div>
        </a>
      </div>
      <div class="club-list-wrapper">
        <a href="${url}" class="club-list-item">
          <div class="club-list-item__logo">
            <div class="club-list-item__placeholder"><i class="fa-solid fa-building text-muted"></i></div>
          </div>
          <div class="flex-grow-1 min-width-0">
            <div class="fw-semibold">${escapeHtml(club.name)}</div>
            <div class="text-muted small">${locationHtml}${club.city && club.members_count ? " &bull; " : ""}<i class="fa-solid fa-users me-1"></i>${membersText}</div>
            <div>${feds}</div>
          </div>
          <div class="flex-shrink-0 text-muted"><i class="fa-solid fa-chevron-right"></i></div>
        </a>
      </div>
    </div>`
  }

  _renderEmpty() {
    return `<div class="text-center py-5">
      <i class="fa-solid fa-building fa-3x text-muted mb-3"></i>
      <h4>${escapeHtml(t("no_results"))}</h4>
      <p class="text-muted">${escapeHtml(t("try_different_filters"))}</p>
    </div>`
  }
}
